import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

// Components
import PostCard from '../../components/post/post-card'
import ReportProfileModal from '../../modals/profile/ReportModal';
import UpdateProfileDetailsModal from '../../modals/profile/UpdateDetails';
import { Loading } from "../../components/Loading";

// Hooks
import useUpdateProfileDetails from  '../../hooks/profile/useUpdateProfileDetailsModal';

import useReportProfile from '../../hooks/profile/useReportModal';
import { useProfileQuery } from '../../services/profileApis';
import { useProfilePostQuery } from '../../services/postApis';

// Services
import { getToken } from '../../storage/LocalStorageService';

const ProfileDetail = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { access_token } = getToken();
  const { t } = useTranslation('profile');
  const translation = t('profile', { returnObjects: true });
  
  // Custom hooks
  const updateProfileModal = useUpdateProfileDetails();
  const reportModal = useReportProfile();
  
  // Queries
  const { 
    data: profileData, 
    isLoading: profileLoading, 
    error: profileError 
  } = useProfileQuery(username);
  
  const { 
    data: posts, 
    isSuccess: postsSuccess,
    isLoading: postsLoading
  } = useProfilePostQuery(username);

  // State management
  const [follow, setFollow] = useState(false);
  
  // Auth protection
  useEffect(() => {
    if (!access_token) {
      navigate("/auth/login");
    }
  }, [access_token, navigate]);
  
  // Initialize follow state once profile data is loaded
  useEffect(() => {
    if (profileData && !profileLoading) {
      setFollow(profileData.follow || false);
    }
  }, [profileData, profileLoading]);

  // Handle follow/unfollow action
  const handleFollowClick = async () => {
    if (!username || profileLoading) return;
    
    const formData = new FormData();
    formData.append("username", username);
    
    // Optimistic UI update
    const newFollowState = !follow;
    setFollow(newFollowState);
    formData.append('action', String(newFollowState));

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_DOMAIN}/user/follow/`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${access_token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
    } catch (error) {
      // Revert on failure
      setFollow(!newFollowState);
    }
  };

  // Loading state
  if (profileLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading />
      </div>
    );
  }
  
  // Error state
  if (profileError || !profileData) {
    return (
      <div className="container mx-auto mt-20 p-6 text-center">
        <h2 className="text-2xl font-bold text-red-500">
          {translation.error_loading}
        </h2>
        <p className="mt-4 text-gray-600">
          {profileError?.message || "Profile could not be loaded"}
        </p>
      </div>
    );
  }

  // User not found case
  if (!profileData?.data?.user?.full_name) {
    return (
      <div className="container mx-auto mt-20 p-6 text-center">
        <h2 className="text-2xl font-bold text-gray-700 dark:text-gray-300">
          {translation.not_found}
        </h2>
        <p className="mt-4 text-gray-600 dark:text-gray-400">
          {username}{translation.profile_removed}
        </p>
      </div>
    );
  }

  const isOwnProfile = profileData.can_edit || false;
  const userData = profileData.data?.user || {};
  const profileDetails = profileData.data || {};

  return (
    <div className="container mx-auto mt-20 p-6">
      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          {/* Profile Avatar */}
          <div className="relative">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24" 
              fill="currentColor" 
              className="size-36 dark:text-white"
              aria-label="Profile avatar"
            >
              <path 
                fillRule="evenodd" 
                d="M18.685 19.097A9.723 9.723 0 0 0 21.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 0 0 3.065 7.097A9.716 9.716 0 0 0 12 21.75a9.716 9.716 0 0 0 6.685-2.653Zm-12.54-1.285A7.486 7.486 0 0 1 12 15a7.486 7.486 0 0 1 5.855 2.812A8.224 8.224 0 0 1 12 20.25a8.224 8.224 0 0 1-5.855-2.438ZM15.75 9a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" 
                clipRule="evenodd" 
              />
            </svg>
          </div>

          {/* Profile Information */}
          <div className="flex-1 text-center md:text-left">
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
              {userData.full_name}
            </h1>
            <p className="text-gray-500 dark:text-gray-300">
              @{userData.username}
            </p>
            {profileDetails.slogan && (
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {profileDetails.slogan}
              </p>
            )}
            {profileDetails.intro && (
              <p className="mt-4 text-gray-700 dark:text-gray-300">
                {profileDetails.intro}
              </p>
            )}

            {/* Action Buttons */}
            <div className="mt-6 flex flex-wrap gap-4 justify-center md:justify-start">
              {!isOwnProfile && (
                <>
                  <button
                    className="px-5 py-2 text-white bg-blue-500 hover:bg-blue-600 rounded-lg shadow-md transition"
                    onClick={handleFollowClick}
                    aria-label={follow ? "Unfollow user" : "Follow user"}
                  >
                    {follow ? translation.unfollow : translation.follow}
                  </button>
                  
                  <button 
                    onClick={() => reportModal.open()} 
                    className="px-5 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-lg shadow-md transition"
                    aria-label="Report user"
                  >
                    {translation.report}
                  </button>
                </>
              )}

              {isOwnProfile && (
                <button
                  onClick={() => updateProfileModal.open()}
                  className="px-5 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-lg shadow-md transition"
                  aria-label="Edit profile"
                >
                  {translation.edit_profile}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Posts Section */}
      <div className="mt-10">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
          {translation.posts}
        </h2>
        
        {postsLoading ? (
          <div className="flex justify-center py-12">
            <Loading />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {postsSuccess && posts?.length > 0 ? (
              posts.map((post) => <PostCard key={post.id} post={post} />)
            ) : (
              <p className="text-gray-500 dark:text-gray-300">
                {translation.no_posts}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {updateProfileModal.isOpen && <UpdateProfileDetailsModal username={username} />}
      {reportModal.isOpen && <ReportProfileModal username={username} />}
    </div>
  );
};

export default ProfileDetail;